/**
 * Template portfolio creation contract (docs/tech/12-template-portfolio-creation.md).
 * One fixed SP structure is compiled from these settings and keyword pools.
 */
import { z } from "zod";
import {
  BiddingStrategySchema,
  CalendarDateSchema,
  type CampaignCreationSubmit,
  type ManualKeywordMatchType,
  PlacementSchema,
} from "./campaign-creation";

export const TemplateKeywordCategorySchema = z.enum([
  "core",
  "long_tail",
  "competitor_brand",
  "misspelling",
  "non_english",
  "negative",
  "discard",
  /** Imported but not yet classified (doc §7.1); never compiled into any campaign. */
  "unclassified",
]);
export type TemplateKeywordCategory = z.infer<typeof TemplateKeywordCategorySchema>;

export const TemplateKeywordSourceSchema = z.enum([
  "excel",
  "ai_classified",
  "ai_generated",
  "user",
]);
export type TemplateKeywordSource = z.infer<typeof TemplateKeywordSourceSchema>;

export const TemplateKeywordMetricsSchema = z.object({
  monthlyPurchases: z.number().nullable(),
  monthlySearchVolume: z.number().nullable(),
  relevance: z.number().nullable(),
  abaMonthlyRank: z.number().nullable(),
  ppcBid: z.number().nullable(),
});
export type TemplateKeywordMetrics = z.infer<typeof TemplateKeywordMetricsSchema>;

export const TemplateKeywordSchema = z.object({
  text: z.string().trim().min(1).max(80),
  category: TemplateKeywordCategorySchema,
  source: TemplateKeywordSourceSchema,
  reason: z.string().nullable(),
  selected: z.boolean(),
  metrics: TemplateKeywordMetricsSchema,
  /** Excel 关键词翻译 / 所属类目 — LLM classification hints only (doc §6.1). */
  translation: z.string().nullable().optional(),
  excelCategory: z.string().nullable().optional(),
});
export type TemplateKeyword = z.infer<typeof TemplateKeywordSchema>;

const TemplateProductSchema = z.object({
  asin: z.string().regex(/^[A-Z0-9]{10}$/),
  sku: z.string().min(1),
});

const TemplateCategoryTargetSchema = z
  .object({
    categoryId: z.string().nullable(),
    categoryName: z.string().nullable(),
    priceGreaterThan: z.number().nonnegative().default(80),
    priceLessThan: z.number().positive().default(150),
  })
  .refine((band) => band.priceGreaterThan < band.priceLessThan, {
    message: "priceGreaterThan must be less than priceLessThan",
    path: ["priceLessThan"],
  });

/** Stable key of one keyword target inside a compiled campaign; overrides refer to it. */
export function templateTargetKey(text: string, matchType: ManualKeywordMatchType): string {
  return `${text.trim().toLowerCase()}|${matchType}`;
}

/**
 * Per-campaign edits made in the structure-preview drawer (Q15). Template values stay
 * the defaults; an override only replaces what the user changed.
 */
export const TemplateCampaignOverrideSchema = z.object({
  name: z.string().trim().min(1).max(128).optional(),
  dailyBudget: z.number().positive().optional(),
  biddingStrategy: BiddingStrategySchema.optional(),
  defaultBid: z.number().positive().optional(),
  placementAdjustments: z
    .array(z.object({ placement: PlacementSchema, percentage: z.number().int().min(0).max(900) }))
    .optional(),
  /** Keyed by templateTargetKey. */
  targetBids: z.record(z.string().max(200), z.number().positive()).optional(),
  /** templateTargetKey values removed from the campaign. */
  removedTargets: z.array(z.string().max(200)).max(10_000).optional(),
  addedNegatives: z.array(z.string().trim().min(1).max(80)).max(1_000).optional(),
  /** Negative keyword texts (case-insensitive) removed from the campaign. */
  removedNegatives: z.array(z.string().max(80)).max(1_000).optional(),
});
export type TemplateCampaignOverride = z.infer<typeof TemplateCampaignOverrideSchema>;

const CampaignOverridesSchema = z
  .record(z.string().min(1).max(200), TemplateCampaignOverrideSchema)
  .optional();

const ExcludedCampaignKeysSchema = z.array(z.string().min(1).max(200)).max(500).optional();

/**
 * Campaign names are `【prefix + ASIN】` + a layer title (longest: 非英语关键词捡漏 + a
 * two-digit index, 10 chars) and must stay within Amazon's 128-character limit.
 */
export const TEMPLATE_NAME_PREFIX_MAX_LENGTH = 100;

export const TemplatePortfolioSettingsSchema = z.object({
  product: TemplateProductSchema,
  namePrefix: z.string().trim().min(1).max(TEMPLATE_NAME_PREFIX_MAX_LENGTH),
  portfolioName: z.string().trim().min(1),
  ownBrand: z.string().nullable(),
  startDate: CalendarDateSchema,
  categoryTarget: TemplateCategoryTargetSchema,
  /** Compiled campaign keys the user deleted in the structure preview; never created. */
  excludedCampaignKeys: ExcludedCampaignKeysSchema,
  /** Keyed by compiled campaign key. */
  campaignOverrides: CampaignOverridesSchema,
});
export type TemplatePortfolioSettings = z.infer<typeof TemplatePortfolioSettingsSchema>;

// ── Draft ───────────────────────────────────────────────────────

/** Max keywords one draft holds (aligned with SMART_CREATION_MAX_BLUEPRINT_ITEMS, doc §6.2). */
export const TEMPLATE_PORTFOLIO_MAX_KEYWORDS = 10_000;

/**
 * In-progress settings: every field the user fills is nullable until preview, which
 * requires the result to satisfy TemplatePortfolioSettingsSchema.
 */
export const TemplatePortfolioDraftSettingsSchema = z.object({
  product: TemplateProductSchema.nullable().default(null),
  namePrefix: z.string().trim().max(TEMPLATE_NAME_PREFIX_MAX_LENGTH).nullable().default(null),
  portfolioName: z.string().trim().max(128).nullable().default(null),
  ownBrand: z.string().trim().max(128).nullable().default(null),
  startDate: CalendarDateSchema.nullable().default(null),
  categoryTarget: TemplateCategoryTargetSchema.default({ categoryId: null, categoryName: null }),
  excludedCampaignKeys: ExcludedCampaignKeysSchema,
  campaignOverrides: CampaignOverridesSchema,
});
export type TemplatePortfolioDraftSettings = z.infer<typeof TemplatePortfolioDraftSettingsSchema>;

export const TemplatePortfolioDraftStatusSchema = z.enum(["draft", "executing", "completed"]);
export type TemplatePortfolioDraftStatus = z.infer<typeof TemplatePortfolioDraftStatusSchema>;

export interface TemplatePortfolioImportedFile {
  fileName: string;
  rowCount: number;
  importedAt: string;
}

export interface TemplatePortfolioDraft {
  id: string;
  shopId: string;
  status: TemplatePortfolioDraftStatus;
  version: number;
  settings: TemplatePortfolioDraftSettings;
  keywords: TemplateKeyword[];
  importedFiles: TemplatePortfolioImportedFile[];
  createdAt: string;
  updatedAt: string;
}

export const TemplatePortfolioDraftCreateInputSchema = z.object({
  settings: TemplatePortfolioDraftSettingsSchema.optional(),
});

/** settings / keywords each replace the stored value wholesale when present. */
export const TemplatePortfolioDraftUpdateInputSchema = z
  .object({
    expectedVersion: z.number().int().positive(),
    settings: TemplatePortfolioDraftSettingsSchema.optional(),
    keywords: z.array(TemplateKeywordSchema).max(TEMPLATE_PORTFOLIO_MAX_KEYWORDS).optional(),
  })
  .refine((input) => input.settings !== undefined || input.keywords !== undefined, {
    message: "settings or keywords is required",
  });
export type TemplatePortfolioDraftUpdateInput = z.infer<
  typeof TemplatePortfolioDraftUpdateInputSchema
>;

const optionalMetric = z.number().finite().nullable().optional();

/**
 * One parsed Excel row (doc §6.1). The keyword is validated per row by the service
 * so an invalid row is reported with its index instead of failing the whole body.
 */
export const TemplateImportRowSchema = z.object({
  keyword: z.string(),
  translation: z.string().nullable().optional(),
  relevance: optionalMetric,
  abaMonthlyRank: optionalMetric,
  monthlySearchVolume: optionalMetric,
  monthlyPurchases: optionalMetric,
  /** Plain number; the client strips the `$` (`$1.44` → 1.44). */
  ppcBid: optionalMetric,
  category: z.string().nullable().optional(),
});
export type TemplateImportRow = z.infer<typeof TemplateImportRowSchema>;

export const TemplatePortfolioImportInputSchema = z.object({
  expectedVersion: z.number().int().positive(),
  fileName: z.string().trim().min(1).max(255),
  rows: z.array(TemplateImportRowSchema).min(1).max(TEMPLATE_PORTFOLIO_MAX_KEYWORDS),
});
export type TemplatePortfolioImportInput = z.infer<typeof TemplatePortfolioImportInputSchema>;

export interface TemplateCategoryCandidates {
  /** path: ancestors joined with " / ", null when Amazon returns none. */
  candidates: { categoryId: string; name: string; path: string | null }[];
  /** false when Amazon could not be reached; the UI falls back to manual category ID input. */
  available: boolean;
}

// ── Compiled plan ───────────────────────────────────────────────

export const TemplatePortfolioLayerSchema = z.enum([
  "core",
  "long_tail",
  "auto_close_match",
  "auto_substitutes",
  "category_target",
  "category_theme",
  "competitor_brand",
  "misspelling",
  "non_english",
]);
export type TemplatePortfolioLayer = z.infer<typeof TemplatePortfolioLayerSchema>;

export const TemplateSkippedLayerReasonSchema = z.enum([
  "empty_pool",
  "missing_category",
  /** No longer produced (theme targets are creatable since 2026-10-13); kept for stored executions. */
  "theme_unsupported",
]);
export type TemplateSkippedLayerReason = z.infer<typeof TemplateSkippedLayerReasonSchema>;

export interface CompiledTemplateCampaign {
  /** Stable across recompiles of the same pools; the executor derives idempotency from it. */
  key: string;
  layer: TemplatePortfolioLayer;
  name: string;
  /** The executor assigns requestId and fills campaign.portfolioId. */
  payload: Omit<CampaignCreationSubmit, "requestId">;
}

export interface CompiledTemplatePortfolio {
  portfolioName: string;
  campaigns: CompiledTemplateCampaign[];
  skippedLayers: { layer: TemplatePortfolioLayer; reason: TemplateSkippedLayerReason }[];
  summary: { campaignCount: number; keywordTargetCount: number };
}

export type TemplatePortfolioDiagnosticCode =
  | "template_portfolio.portfolio.name_conflict"
  | "template_portfolio.campaign.duplicate_name"
  | "template_portfolio.campaign.name_truncated"
  /** Drawer edits removed every keyword target; Amazon needs at least one. */
  | "template_portfolio.campaign.no_targets"
  /** The selected negative pool exceeds what one Amazon campaign accepts. */
  | "template_portfolio.negatives.limit_exceeded"
  /** Overrides whose campaign no longer exists after the keyword pools changed. */
  | "template_portfolio.overrides.stale";

export interface TemplatePortfolioDiagnostic {
  code: TemplatePortfolioDiagnosticCode;
  message: string;
  /** Compiled campaign keys the diagnostic refers to; empty for portfolio-level ones. */
  campaignKeys: string[];
}

export interface TemplatePortfolioPreview {
  draftVersion: number;
  portfolio: CompiledTemplatePortfolio;
  diagnostics: TemplatePortfolioDiagnostic[];
}

// ── Execution ───────────────────────────────────────────────────

export const TemplatePortfolioExecuteInputSchema = z.object({
  expectedVersion: z.number().int().positive(),
});
export type TemplatePortfolioExecuteInput = z.infer<typeof TemplatePortfolioExecuteInputSchema>;

export const TemplatePortfolioExecutionStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "partial",
  "failed",
]);
export type TemplatePortfolioExecutionStatus = z.infer<
  typeof TemplatePortfolioExecutionStatusSchema
>;

export const TemplatePortfolioExecutionItemStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
  /** A layer the compiled plan skipped (empty pool, missing category, theme unsupported). */
  "skipped",
]);
export type TemplatePortfolioExecutionItemStatus = z.infer<
  typeof TemplatePortfolioExecutionItemStatusSchema
>;

/** 一键开启 (docs/tech/12 §3 ⑤) progress of one created campaign. */
export const TemplatePortfolioEnableStatusSchema = z.enum([
  "not_requested",
  "pending",
  "running",
  "enabled",
  "failed",
]);
export type TemplatePortfolioEnableStatus = z.infer<typeof TemplatePortfolioEnableStatusSchema>;

export interface TemplatePortfolioExecutionItem {
  id: string;
  /** Compiled campaign key; `skipped:{layer}` for skipped layers. */
  campaignKey: string;
  /** null for skipped layers, which never had a compiled campaign. */
  campaignName: string | null;
  layer: TemplatePortfolioLayer;
  ordinal: number;
  status: TemplatePortfolioExecutionItemStatus;
  skipReason: TemplateSkippedLayerReason | null;
  amazonCampaignId: string | null;
  localCampaignId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  /** Product ads → ad group → campaign set ENABLED; only `succeeded` items leave `not_requested`. */
  enableStatus: TemplatePortfolioEnableStatus;
  /** `template_portfolio.enable.{product_ads|ad_group|campaign}_failed` names the write that failed. */
  enableErrorCode: string | null;
  enableErrorMessage: string | null;
  enabledAt: string | null;
}

export interface TemplatePortfolioExecution {
  id: string;
  draftId: string;
  status: TemplatePortfolioExecutionStatus;
  portfolio: {
    name: string;
    amazonPortfolioId: string | null;
    /** Local portfolio id — the result page links to the portfolio detail with it. */
    portfolioId: string | null;
  };
  errorCode: string | null;
  errorMessage: string | null;
  counts: Record<TemplatePortfolioExecutionItemStatus, number> & {
    total: number;
    enablePending: number;
    enabling: number;
    enabled: number;
    enableFailed: number;
  };
  /** First 一键开启 request; null until the user enables. */
  enableRequestedAt: string | null;
  items: TemplatePortfolioExecutionItem[];
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
}
